import { useMemo } from 'react'
import {
  CellStyle,
  ColDef,
  ColGroupDef,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams,
  ValueParserParams,
  ValueSetterParams,
} from 'ag-grid-community'
import { intl } from '../../../../i18n'
import {
  ColumnType,
  dateValueFormatter,
} from '../../../containers/commons/AgGrid'
import { ProgramManagementStatusCellRenderer } from '../../../features/ProgramManagementStatusCellRenderer'
import { ClientSideSelectFilter } from '../../../containers/BulkSheetView/components/filter'
import { colorPalette } from '../../../style/colorPalette'
import { useRowNumber } from '../../../page-properties/bulksheet-properties/rowNumber'
import { useCode } from '../../../page-properties/bulksheet-properties/common'
import {
  useCreatedAt,
  useCreatedBy,
  useRevision,
  useUpdatedAt,
  useUpdatedBy,
} from '../../../page-properties/bulksheet-properties/changeLog'
import {
  emptyProgress,
  isProgressConsistent,
  PROGRAM_PROJECT_REPORT_INPUT_MODE,
  PROGRAM_PROJECT_REPORT_ROW_TYPE,
  programProjectReportRowService,
  ProgramProjectReportInputMode,
  ProgramProjectReportProfitLoss,
  ProgramProjectReportProgress,
  ProgramProjectReportResource,
  ProgramProjectReportRow,
  ProgramProjectReportRowType,
} from '../model/reportRow'
import {
  ProductivityMetric,
  calcExpectedProfitRatio,
  calcPreceding,
  calcProductivity,
  calcProgressRate,
  calcRemaining,
  calcRemainingBudget,
  calcScheduledProgressRate,
  calcScheduledResource,
} from './calc'
import {
  AggregateField,
  WbsItemStatus,
  toLabel as toWbsStatusLabel,
} from '../../../../domain/entity/WbsItemEntity'
import { ProjectStatus } from '../../../../lib/functions/project'
import { WorkLoadUnitState } from '../../../hooks/useWorkloadUnit'

type RowValueGetter = ValueGetterParams<ProgramProjectReportRow>

type ReportGridContext = {
  workloadUnitState?: WorkLoadUnitState
  aggregateField?: AggregateField
  inputMode?: ProgramProjectReportInputMode
  allRows?: ProgramProjectReportRow[]
  dateFormat?: string
}

const readContext = (context: unknown): ReportGridContext =>
  (context as ReportGridContext | undefined) ?? {}

const readUnit = (
  context: unknown
): { factor: number; isWorkload: boolean } => {
  const { workloadUnitState, aggregateField } = readContext(context)
  return {
    factor: workloadUnitState?.hoursPerSelectedUnit || 1,
    isWorkload: aggregateField === AggregateField.WBS_ITEM_WORKLOAD,
  }
}

const NUMBER_COLUMN_TYPE = [ColumnType.number]
const DATE_COLUMN_TYPE = [ColumnType.date]

const wbsStatusLabelByValue: Record<string, string> = Object.fromEntries(
  Object.values(WbsItemStatus).map(status => [status, toWbsStatusLabel(status)])
)

export const statusFilterOptions = [
  ...Object.values(ProjectStatus),
  ...Object.values(WbsItemStatus).map(toWbsStatusLabel),
]

const statusSelectFilter = {
  filter: ClientSideSelectFilter,
  filterParams: {
    optionKey: 'programProjectReportStatuses',
    getValuesForOptionFilter: async (context: {
      programProjectReportStatuses: string[]
    }) => context.programProjectReportStatuses,
    getValue: (option: string) => option,
    getLabel: (option: string) => option,
  },
}

const GRAYED_OUT_STYLE: CellStyle = {
  backgroundColor: colorPalette.monotone[2],
  color: colorPalette.monotone[3],
  justifyContent: 'flex-end',
}

const RIGHT_ALIGN_STYLE: CellStyle = { justifyContent: 'flex-end' }

const EDITABLE_CELL_STYLE: CellStyle = {
  justifyContent: 'flex-end',
  backgroundColor: colorPalette.skyBlue[1],
}

const INCONSISTENT_STYLE: CellStyle = {
  justifyContent: 'flex-end',
  backgroundColor: colorPalette.pink[1],
}

const isNonEditableProgressCell = (params: {
  node?: { allChildrenCount?: number | null } | null
  context?: unknown
}): boolean => {
  const { inputMode } = readContext(params.context)
  if (inputMode === PROGRAM_PROJECT_REPORT_INPUT_MODE.AGGREGATION) {
    return !!params.node?.allChildrenCount
  }
  return false
}

const progressCellStyle =
  (
    field: keyof ProgramProjectReportProgress,
    options?: { grayOutOnCount?: boolean }
  ) =>
  (params: {
    data?: ProgramProjectReportRow | null
    node?: { allChildrenCount?: number | null } | null
    context?: unknown
  }): CellStyle => {
    const ctx = readContext(params.context)
    if (
      options?.grayOutOnCount &&
      ctx.aggregateField === AggregateField.WBS_ITEM_COUNT
    ) {
      return GRAYED_OUT_STYLE
    }
    if (
      ctx?.inputMode === PROGRAM_PROJECT_REPORT_INPUT_MODE.FREE &&
      ctx?.allRows &&
      params.data &&
      !isProgressConsistent(ctx.allRows, params.data, field)
    ) {
      return INCONSISTENT_STYLE
    }
    return isNonEditableProgressCell(params)
      ? RIGHT_ALIGN_STYLE
      : EDITABLE_CELL_STYLE
  }

const projectOnlyEditableCellStyle = (params: {
  data?: ProgramProjectReportRow | null
}): CellStyle =>
  programProjectReportRowService.isProject(params.data)
    ? EDITABLE_CELL_STYLE
    : GRAYED_OUT_STYLE

const projectOnlyCellStyle = (params: {
  data?: ProgramProjectReportRow | null
}): CellStyle =>
  programProjectReportRowService.isProject(params.data)
    ? RIGHT_ALIGN_STYLE
    : GRAYED_OUT_STYLE

const numberValueFormatter = (params: ValueFormatterParams): string =>
  params.value == null ? '' : Number(params.value).toLocaleString()

const decimalValueFormatter = (params: ValueFormatterParams): string =>
  params.value == null
    ? ''
    : Number(params.value).toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })

const percentValueFormatter = (params: ValueFormatterParams): string =>
  params.value == null ? '' : `${(Number(params.value) * 100).toFixed(1)}%`

const getProgress = (
  data?: ProgramProjectReportRow | null
): ProgramProjectReportProgress | null => data?.progress ?? null

const getResource = (
  data?: ProgramProjectReportRow | null
): ProgramProjectReportResource | null => data?.resource ?? null

const getProfitLoss = (
  data?: ProgramProjectReportRow | null
): ProgramProjectReportProfitLoss | null => data?.profitLoss ?? null

const toDisplayWorkload = (
  hour: number | null,
  context: unknown,
  options?: { alwaysWorkload?: boolean }
): number | null => {
  if (hour == null) return null
  const { factor, isWorkload } = readUnit(context)
  return options?.alwaysWorkload || isWorkload ? hour / factor : hour
}

const workloadDecimalOrCountFormatter = (
  params: ValueFormatterParams
): string =>
  readUnit(params.context).isWorkload
    ? decimalValueFormatter(params)
    : numberValueFormatter(params)

const isProgressEditableRow = (
  data?: ProgramProjectReportRow | null
): boolean =>
  data?.type === PROGRAM_PROJECT_REPORT_ROW_TYPE.PROJECT ||
  data?.type === PROGRAM_PROJECT_REPORT_ROW_TYPE.WORKGROUP ||
  data?.type === PROGRAM_PROJECT_REPORT_ROW_TYPE.PROCESS

type ProgressEditabilityParams = {
  data?: ProgramProjectReportRow | null
  node?: { allChildrenCount?: number | null } | null
  context?: unknown
}

const isProgressCellEditable = (params: ProgressEditabilityParams): boolean => {
  if (!isProgressEditableRow(params.data)) return false
  const { inputMode } = readContext(params.context)
  if (inputMode !== PROGRAM_PROJECT_REPORT_INPUT_MODE.AGGREGATION) return true
  return !params.node?.allChildrenCount
}

const parseNumberInput = (raw: unknown): number | null | false => {
  if (raw === '' || raw === null || raw === undefined) return null
  const num = Number(raw)
  return Number.isNaN(num) ? false : num
}

const numberValueParser = (
  params: ValueParserParams<ProgramProjectReportRow>
): number | null => {
  const parsed = parseNumberInput(params.newValue)
  return parsed === false ? (params.oldValue ?? null) : parsed
}

const toStoredHour = (
  parsed: number | null,
  context: unknown,
  options?: { alwaysWorkload?: boolean }
): number | null => {
  if (parsed === null) return null
  const { factor, isWorkload } = readUnit(context)
  return options?.alwaysWorkload || isWorkload ? parsed * factor : parsed
}

const setProgressValue =
  (field: keyof ProgramProjectReportProgress) =>
  (params: ValueSetterParams<ProgramProjectReportRow>): boolean => {
    if (!params.data) return false
    const parsed = parseNumberInput(params.newValue)
    if (parsed === false) return false
    const hourValue = toStoredHour(parsed, params.context)
    const progress = getProgress(params.data) ?? emptyProgress()
    if (progress[field] === hourValue) return false
    progress[field] = hourValue
    params.data.progress = progress
    return true
  }

const setResourceValue =
  (field: keyof ProgramProjectReportResource) =>
  (params: ValueSetterParams<ProgramProjectReportRow>): boolean => {
    if (!params.data) return false
    const parsed = parseNumberInput(params.newValue)
    if (parsed === false) return false
    const hourValue = toStoredHour(parsed, params.context, {
      alwaysWorkload: true,
    })
    const resource = getResource(params.data) ?? {
      totalResource: null,
      investedResource: null,
      actualResource: null,
    }
    if (resource[field] === hourValue) return false
    resource[field] = hourValue
    params.data.resource = resource
    return true
  }

const setProfitLossValue =
  (field: keyof ProgramProjectReportProfitLoss) =>
  (params: ValueSetterParams<ProgramProjectReportRow>): boolean => {
    if (!params.data) return false
    const parsed = parseNumberInput(params.newValue)
    if (parsed === false) return false
    const profitLoss = getProfitLoss(params.data) ?? {
      totalBudget: null,
      scheduledUsedBudget: null,
      actualUsedBudget: null,
      totalRevenue: null,
      scheduledRevenue: null,
      actualRevenue: null,
      forecastRevenue: null,
    }
    if (profitLoss[field] === parsed) return false
    profitLoss[field] = parsed
    params.data.profitLoss = profitLoss
    return true
  }

const typeLabelKey: Record<ProgramProjectReportRowType, string> = {
  [PROGRAM_PROJECT_REPORT_ROW_TYPE.PROJECT]:
    'programProjectReport.type.project',
  [PROGRAM_PROJECT_REPORT_ROW_TYPE.WORKGROUP]:
    'programProjectReport.type.workgroup',
  [PROGRAM_PROJECT_REPORT_ROW_TYPE.PROCESS]:
    'programProjectReport.type.process',
}

const typeSelectFilter = {
  filter: ClientSideSelectFilter,
  filterParams: {
    optionKey: 'programProjectReportTypes',
    getValuesForOptionFilter: async (context: {
      programProjectReportTypes: ProgramProjectReportRowType[]
    }) => context.programProjectReportTypes,
    valueGetter: ({ node }: { node: IRowNode<ProgramProjectReportRow> }) =>
      node?.data?.type ?? '',
    getValue: (option: string) => option,
    getLabel: (option: ProgramProjectReportRowType) =>
      intl.formatMessage({ id: typeLabelKey[option] }),
  },
}

const buildBasicColumnGroup = (
  codeCol: ColDef<ProgramProjectReportRow>
): ColGroupDef<ProgramProjectReportRow> => ({
  headerName: intl.formatMessage({ id: 'programProjectReport.column.basic' }),
  children: [
    { ...codeCol, colId: 'code', hide: false, width: 100, pinned: 'left' },
    {
      colId: 'type',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.type',
      }),
      width: 110,
      pinned: 'left',
      valueGetter: (params: RowValueGetter) => {
        const type = params.data?.type
        return type ? intl.formatMessage({ id: typeLabelKey[type] }) : ''
      },
      ...typeSelectFilter,
    },
    {
      colId: 'status',
      field: 'status',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.status',
      }),
      width: 110,
      pinned: 'left',
      cellRenderer: ProgramManagementStatusCellRenderer,
      valueGetter: (params: RowValueGetter) => {
        const { data } = params
        const status = data?.status
        if (!status) return ''
        if (programProjectReportRowService.isProject(data)) {
          return status
        }
        return wbsStatusLabelByValue[status] ?? status
      },
      ...statusSelectFilter,
    },
  ],
})

const periodColumnGroup: ColGroupDef<ProgramProjectReportRow> = {
  headerName: intl.formatMessage({ id: 'programProjectReport.column.period' }),
  children: [
    {
      colId: 'scheduledStartDate',
      field: 'scheduledStartDate',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.scheduledStartDate',
      }),
      type: DATE_COLUMN_TYPE,
      width: 110,
      valueFormatter: params =>
        dateValueFormatter(
          params.value,
          readContext(params.context).dateFormat
        ) || '',
    },
    {
      colId: 'scheduledEndDate',
      field: 'scheduledEndDate',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.scheduledEndDate',
      }),
      type: DATE_COLUMN_TYPE,
      width: 110,
      valueFormatter: params =>
        dateValueFormatter(
          params.value,
          readContext(params.context).dateFormat
        ) || '',
    },
  ],
}

const progressValueColumn = (
  field: keyof ProgramProjectReportProgress,
  headerId: string,
  width: number
): ColDef<ProgramProjectReportRow> => ({
  colId: `progress.${field}`,
  headerName: intl.formatMessage({ id: headerId }),
  type: NUMBER_COLUMN_TYPE,
  width,
  editable: isProgressCellEditable,
  cellStyle: progressCellStyle(field),
  valueGetter: (params: RowValueGetter) =>
    toDisplayWorkload(
      getProgress(params.data)?.[field] ?? null,
      params.context
    ),
  valueParser: numberValueParser,
  valueSetter: setProgressValue(field),
  valueFormatter: workloadDecimalOrCountFormatter,
})

const progressColumnGroup: ColGroupDef<ProgramProjectReportRow> = {
  headerName: intl.formatMessage({
    id: 'programProjectReport.column.progress',
  }),
  children: [
    {
      colId: 'progress.scheduledProgressRate',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.progress.scheduledProgressRate',
      }),

      type: NUMBER_COLUMN_TYPE,
      width: 90,
      cellStyle: RIGHT_ALIGN_STYLE,
      valueGetter: (params: RowValueGetter) => {
        const progress = getProgress(params.data)
        return calcScheduledProgressRate(
          progress?.scheduledCompleted ?? null,
          progress?.total ?? null
        )
      },
      valueFormatter: percentValueFormatter,
    },
    {
      colId: 'progress.progressRate',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.progress.progressRate',
      }),

      type: NUMBER_COLUMN_TYPE,
      width: 80,
      cellStyle: RIGHT_ALIGN_STYLE,
      valueGetter: (params: RowValueGetter) => {
        const progress = getProgress(params.data)
        return calcProgressRate(
          progress?.completed ?? null,
          progress?.total ?? null
        )
      },
      valueFormatter: percentValueFormatter,
    },
    progressValueColumn(
      'total',
      'programProjectReport.column.progress.total',
      70
    ),
    progressValueColumn(
      'scheduledCompleted',
      'programProjectReport.column.progress.scheduledCompleted',
      80
    ),
    progressValueColumn(
      'completed',
      'programProjectReport.column.progress.completed',
      70
    ),
    progressValueColumn(
      'delayed',
      'programProjectReport.column.progress.delayed',
      70
    ),
    {
      colId: 'progress.preceding',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.progress.preceding',
      }),

      type: NUMBER_COLUMN_TYPE,
      width: 70,
      cellStyle: RIGHT_ALIGN_STYLE,
      valueGetter: (params: RowValueGetter) => {
        const progress = getProgress(params.data)
        const value = calcPreceding(
          progress?.completed ?? null,
          progress?.scheduledCompleted ?? null,
          progress?.delayed ?? null
        )
        return toDisplayWorkload(value, params.context)
      },
      valueFormatter: workloadDecimalOrCountFormatter,
    },
    {
      colId: 'progress.remaining',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.progress.remaining',
      }),

      type: NUMBER_COLUMN_TYPE,
      width: 70,
      cellStyle: RIGHT_ALIGN_STYLE,
      valueGetter: (params: RowValueGetter) => {
        const progress = getProgress(params.data)
        const remaining = calcRemaining(
          progress?.total ?? null,
          progress?.completed ?? null
        )
        return toDisplayWorkload(remaining, params.context)
      },
      valueFormatter: workloadDecimalOrCountFormatter,
    },
  ],
}

const productivityColumn = (
  metric: ProductivityMetric,
  headerId: string,
  formatter: (params: ValueFormatterParams) => string,
  convertWorkload: boolean
): ColDef<ProgramProjectReportRow> => ({
  colId: `productivity.${metric}`,
  headerName: intl.formatMessage({ id: headerId }),
  type: NUMBER_COLUMN_TYPE,
  width: 80,
  cellStyle: (params: {
    data?: ProgramProjectReportRow | null
    context?: unknown
  }): CellStyle => {
    if (!programProjectReportRowService.isProject(params.data)) {
      return GRAYED_OUT_STYLE
    }
    const { aggregateField } = readContext(params.context)
    if (aggregateField === AggregateField.WBS_ITEM_COUNT) {
      return GRAYED_OUT_STYLE
    }
    return RIGHT_ALIGN_STYLE
  },
  valueGetter: (params: RowValueGetter) => {
    const progress = getProgress(params.data)
    const value = calcProductivity(metric, {
      total: progress?.total ?? null,
      scheduledCompleted: progress?.scheduledCompleted ?? null,
      completed: progress?.completed ?? null,
      actualResource: progress?.actualResource ?? null,
    })
    return convertWorkload
      ? toDisplayWorkload(value, params.context, { alwaysWorkload: true })
      : value
  },
  valueFormatter: formatter,
})

const productivityColumnGroup: ColGroupDef<ProgramProjectReportRow> = {
  headerName: intl.formatMessage({
    id: 'programProjectReport.column.productivity',
  }),
  children: [
    {
      colId: 'progress.actualResource',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.resource.actualResource',
      }),
      type: NUMBER_COLUMN_TYPE,
      width: 90,
      editable: (params: ProgressEditabilityParams): boolean => {
        const { aggregateField } = readContext(params.context)
        if (aggregateField === AggregateField.WBS_ITEM_COUNT) return false
        return isProgressCellEditable(params)
      },
      cellStyle: progressCellStyle('actualResource', { grayOutOnCount: true }),
      valueGetter: (params: RowValueGetter) =>
        toDisplayWorkload(
          getProgress(params.data)?.actualResource ?? null,
          params.context,
          { alwaysWorkload: true }
        ),
      valueParser: numberValueParser,
      valueSetter: (
        params: ValueSetterParams<ProgramProjectReportRow>
      ): boolean => {
        if (!params.data) return false
        const parsed = parseNumberInput(params.newValue)
        if (parsed === false) return false
        const hourValue = toStoredHour(parsed, params.context, {
          alwaysWorkload: true,
        })
        const progress = getProgress(params.data) ?? emptyProgress()
        if (progress.actualResource === hourValue) return false
        progress.actualResource = hourValue
        params.data.progress = progress
        return true
      },
      valueFormatter: decimalValueFormatter,
    },
    productivityColumn(
      'sv',
      'programProjectReport.column.productivity.sv',
      decimalValueFormatter,
      true
    ),
    productivityColumn(
      'spi',
      'programProjectReport.column.productivity.spi',
      percentValueFormatter,
      false
    ),
    productivityColumn(
      'cv',
      'programProjectReport.column.productivity.cv',
      decimalValueFormatter,
      true
    ),
    productivityColumn(
      'cpi',
      'programProjectReport.column.productivity.cpi',
      percentValueFormatter,
      false
    ),
    productivityColumn(
      'etc',
      'programProjectReport.column.productivity.etc',
      decimalValueFormatter,
      true
    ),
    productivityColumn(
      'eac',
      'programProjectReport.column.productivity.eac',
      decimalValueFormatter,
      true
    ),
  ],
}

const resourceColumn = (
  field: keyof ProgramProjectReportResource,
  headerId: string
): ColDef<ProgramProjectReportRow> => ({
  colId: `resource.${field}`,
  headerName: intl.formatMessage({ id: headerId }),
  type: NUMBER_COLUMN_TYPE,
  width: 90,
  editable: params => programProjectReportRowService.isProject(params.data),
  cellStyle: projectOnlyEditableCellStyle,
  valueGetter: (params: RowValueGetter) => {
    if (!programProjectReportRowService.isProject(params.data)) return null
    return toDisplayWorkload(
      getResource(params.data)?.[field] ?? null,
      params.context,
      { alwaysWorkload: true }
    )
  },
  valueParser: numberValueParser,
  valueSetter: setResourceValue(field),
  valueFormatter: decimalValueFormatter,
})

const resourceColumnGroup: ColGroupDef<ProgramProjectReportRow> = {
  headerName: intl.formatMessage({
    id: 'programProjectReport.column.resource',
  }),
  children: [
    resourceColumn(
      'totalResource',
      'programProjectReport.column.resource.totalResource'
    ),
    resourceColumn(
      'investedResource',
      'programProjectReport.column.resource.investedResource'
    ),
    {
      colId: 'resource.scheduledResource',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.resource.scheduledResource',
      }),

      type: NUMBER_COLUMN_TYPE,
      width: 90,
      cellStyle: projectOnlyCellStyle,
      valueGetter: (params: RowValueGetter) => {
        if (!programProjectReportRowService.isProject(params.data)) return null
        const resource = getResource(params.data)
        const value = calcScheduledResource(
          resource?.totalResource ?? null,
          resource?.investedResource ?? null
        )
        return toDisplayWorkload(value, params.context, {
          alwaysWorkload: true,
        })
      },
      valueFormatter: decimalValueFormatter,
    },
  ],
}

const profitLossColumn = (
  field: keyof ProgramProjectReportProfitLoss,
  headerId: string
): ColDef<ProgramProjectReportRow> => ({
  colId: `profitLoss.${field}`,
  headerName: intl.formatMessage({ id: headerId }),
  type: NUMBER_COLUMN_TYPE,
  width: 100,
  editable: params => programProjectReportRowService.isProject(params.data),
  cellStyle: projectOnlyEditableCellStyle,
  valueGetter: (params: RowValueGetter) => {
    if (!programProjectReportRowService.isProject(params.data)) return null
    return getProfitLoss(params.data)?.[field] ?? null
  },
  valueParser: numberValueParser,
  valueSetter: setProfitLossValue(field),
  valueFormatter: numberValueFormatter,
})

const profitLossColumnGroup: ColGroupDef<ProgramProjectReportRow> = {
  headerName: intl.formatMessage({
    id: 'programProjectReport.column.profitLoss',
  }),
  children: [
    profitLossColumn(
      'totalBudget',
      'programProjectReport.column.profitLoss.totalBudget'
    ),
    profitLossColumn(
      'scheduledUsedBudget',
      'programProjectReport.column.profitLoss.scheduledUsedBudget'
    ),
    profitLossColumn(
      'actualUsedBudget',
      'programProjectReport.column.profitLoss.actualUsedBudget'
    ),
    {
      colId: 'profitLoss.remainingBudget',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.profitLoss.remainingBudget',
      }),

      type: NUMBER_COLUMN_TYPE,
      width: 100,
      cellStyle: projectOnlyCellStyle,
      valueGetter: (params: RowValueGetter) => {
        if (!programProjectReportRowService.isProject(params.data)) return null
        const profitLoss = getProfitLoss(params.data)
        return calcRemainingBudget(
          profitLoss?.totalBudget ?? null,
          profitLoss?.actualUsedBudget ?? null
        )
      },
      valueFormatter: numberValueFormatter,
    },
    profitLossColumn(
      'totalRevenue',
      'programProjectReport.column.profitLoss.totalRevenue'
    ),
    profitLossColumn(
      'scheduledRevenue',
      'programProjectReport.column.profitLoss.scheduledRevenue'
    ),
    profitLossColumn(
      'actualRevenue',
      'programProjectReport.column.profitLoss.actualRevenue'
    ),
    profitLossColumn(
      'forecastRevenue',
      'programProjectReport.column.profitLoss.forecastRevenue'
    ),
    {
      colId: 'profitLoss.expectedProfitRatio',
      headerName: intl.formatMessage({
        id: 'programProjectReport.column.profitLoss.expectedProfitRatio',
      }),

      type: NUMBER_COLUMN_TYPE,
      width: 100,
      cellStyle: projectOnlyCellStyle,
      valueGetter: (params: RowValueGetter) => {
        if (!programProjectReportRowService.isProject(params.data)) return null
        const profitLoss = getProfitLoss(params.data)
        return calcExpectedProfitRatio(
          profitLoss?.forecastRevenue ?? null,
          profitLoss?.actualUsedBudget ?? null,
          calcRemainingBudget(
            profitLoss?.totalBudget ?? null,
            profitLoss?.actualUsedBudget ?? null
          )
        )
      },
      valueFormatter: percentValueFormatter,
    },
  ],
}

export const useColumnDefs = (): (
  | ColDef<ProgramProjectReportRow>
  | ColGroupDef<ProgramProjectReportRow>
)[] => {
  const rowNumberCol = useRowNumber()
  const codeCol = useCode('code')
  const revisionCol = useRevision('revision')
  const createdAtCol = useCreatedAt('createdAt')
  const createdByCol = useCreatedBy('createdBy')
  const updatedAtCol = useUpdatedAt('updatedAt')
  const updatedByCol = useUpdatedBy('updatedBy')

  return useMemo(
    () => [
      { field: 'uuid', hide: true, suppressColumnsToolPanel: true },
      rowNumberCol,
      buildBasicColumnGroup(codeCol),
      periodColumnGroup,
      progressColumnGroup,
      productivityColumnGroup,
      resourceColumnGroup,
      profitLossColumnGroup,
      {
        headerName: intl.formatMessage({ id: 'changeLog' }),
        children: [
          { ...revisionCol, cellStyle: projectOnlyCellStyle },
          { ...createdAtCol, cellStyle: projectOnlyCellStyle },
          { ...createdByCol, cellStyle: projectOnlyCellStyle },
          { ...updatedAtCol, cellStyle: projectOnlyCellStyle },
          { ...updatedByCol, cellStyle: projectOnlyCellStyle },
        ],
      },
    ],
    [
      rowNumberCol,
      codeCol,
      revisionCol,
      createdAtCol,
      createdByCol,
      updatedAtCol,
      updatedByCol,
    ]
  )
}
